import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, X } from 'lucide-react';
import { TeddyBear } from './icons/TeddyBear';
import { motion, AnimatePresence } from 'framer-motion';

interface PlotTwist {
  id: string;
  text: string;
}

interface CollaborativeStoryModeProps {
  isEnabled: boolean;
  plotTwists: PlotTwist[];
  onAddPlotTwist: (twist: string) => void;
  onRemovePlotTwist: (id: string) => void;
  onToggleMode: () => void;
}

export function CollaborativeStoryMode({
  isEnabled,
  plotTwists,
  onAddPlotTwist,
  onRemovePlotTwist,
  onToggleMode
}: CollaborativeStoryModeProps) {
  const [newTwist, setNewTwist] = useState('');
  const [isAddingTwist, setIsAddingTwist] = useState(false);

  const handleAddTwist = () => {
    if (newTwist.trim()) {
      onAddPlotTwist(newTwist.trim());
      setNewTwist('');
      setIsAddingTwist(false);
    }
  };

  return (
    <Card className="border-2 border-dashed border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
                            <img 
                              src="/logo.png" 
                              alt="SnuggleCrafters Logo" 
                              className="w-5 h-5 object-contain"
                            />
            <CardTitle className="text-lg">Collaborative Story Mode</CardTitle>
          </div>
          <Button
            variant={isEnabled ? "default" : "outline"}
            size="sm"
            onClick={onToggleMode}
          >
            {isEnabled ? 'Enabled' : 'Enable'}
          </Button>
        </div>
        <CardDescription>
          Add plot twists to make the story more exciting! The AI will weave your ideas into the narrative.
        </CardDescription>
      </CardHeader>

      {isEnabled && (
        <CardContent className="space-y-4">
          {/* Plot Twists List */}
          <div className="space-y-2">
            <AnimatePresence>
              {plotTwists.map((twist) => (
                <motion.div
                  key={twist.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-start gap-2 p-3 bg-white rounded-lg shadow-sm">
                    <Badge variant="secondary" className="mt-0.5">Twist</Badge>
                    <p className="flex-1 text-sm">{twist.text}</p>
                    <button
                      onClick={() => onRemovePlotTwist(twist.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Add New Twist */}
          {isAddingTwist ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              <Textarea
                value={newTwist}
                onChange={(e) => setNewTwist(e.target.value)}
                placeholder="E.g., 'A friendly dragon appears and needs help finding its way home' or 'The main character discovers they can talk to animals'"
                className="min-h-[80px] resize-none"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddTwist} disabled={!newTwist.trim()}>
                  Add Twist
                </Button>
                <Button
                  size="sm"
                  variant="gradient"
                  onClick={() => {
                    setIsAddingTwist(false);
                    setNewTwist('');
                  }}
                  className="text-white"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          ) : (
            <Button
              variant="gradient"
              size="sm"
              onClick={() => setIsAddingTwist(true)}
              className="w-full text-white"
              disabled={plotTwists.length >= 3}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Plot Twist {plotTwists.length > 0 && `(${plotTwists.length}/3)`}
            </Button>
          )}

          {plotTwists.length >= 3 && (
            <p className="text-xs text-secondary text-center">
              Maximum of 3 plot twists reached
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}